import { EventEmitter } from '@angular/core';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  ICoreDataEntity,
  ObjectTypeInfoColumnDataType,
  ObjectTypeInfoViewModel,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryObjectsModel,
  TargetColumnValue,
  ValidateModel,
} from '@barthauer/abc/core/data';
import { DialogResult, MessageBoxButtons, MessageBoxIcon, MessageMode, MessageSettings } from '@barthauer/abc/messages';
import {
  AbcDropDownBoxComponent,
  convertDataType,
  GridAdapter,
  SortOrder,
  ToastType,
  _AbcGridColumn,
  _AbcGridComponent,
  AbcCoreComponent,
  AbcDateBoxComponent,
} from '@barthauer/abc/ui-components';
import {
  EditCanceledEvent,
  EditingStartEvent,
  InitNewRowEvent,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
} from 'devextreme/ui/data_grid';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ArenaService } from '../../../arena.service';
import { SubLayoutViewModel } from '../../../viewModels/layout.viewModel';
import { DataFormService, IGridOnForm } from '../../data-form.service';
import { DataFormSettings } from '../settings.model';
import { UiCreatorBase } from './ui-creator-base';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

export class GridOnFormUiCreator extends UiCreatorBase {
  //private ensureSavingOnHignaIdChange: () => Promise<void>;
  constructor(
    private service: DataFormService,
    private settings: DataFormSettings,
    private arenaService: ArenaService,
  ) {
    super(service, settings);
  }

  public async _createGridOnForm(
    td: HTMLTableCellElement,
    subLayoutView: SubLayoutViewModel,
    rowHeight: string,
    groupIndex: number,
    subLayoutViewIndex: number,
  ): Promise<void> {
    const abcGrid = await this.createAbcGrid(subLayoutView, groupIndex, subLayoutViewIndex);
    const htmlElement: HTMLElement = abcGrid.domElement;
    htmlElement.style.height = rowHeight;
    td.style.verticalAlign = 'unset';
    await td.insertAdjacentElement('afterbegin', htmlElement);
    if (subLayoutView.caption) {
      td.insertBefore(this.createGroupCaption(td, subLayoutView), htmlElement);
    }
  }

  private async createAbcGrid(subLayoutView: SubLayoutViewModel, groupIndex?: number, subLayoutViewIndex?: number) {
    const abcGrid = this.service.abcContainer.createComponent(this.tabsDirective, _AbcGridComponent);
    const abcGridInstance: _AbcGridComponent = <_AbcGridComponent>abcGrid.componentRef.instance;
    const denyInsert = this.settings.layoutViewModel.denyInsert;
    const denyDelete = this.settings.layoutViewModel.denyDelete;
    const denyUpdate = this.settings.layoutViewModel.denyUpdate;
    const columns: _AbcGridColumn[] = [];
    const gridOnFormIndex =
      groupIndex || groupIndex === 0 ? groupIndex + '_' + subLayoutViewIndex : subLayoutViewIndex.toString();

    abcGrid.domElement.className = 'abcGridOnForm';
    abcGridInstance.simpleMode = true;
    abcGridInstance.dateSerializationFormat = 'yyyy-MM-ddTHH:mm:ss'; // Enforce using date and time without timezone
    abcGridInstance.hoverStateEnabled = true;
    abcGridInstance.rowAlternationEnabled = true;
    abcGridInstance.rtlEnabled = this.arenaService.rtlEnabled;

    let namedColumns = {};
    let refObjectType: string = this.settings.mainObjectTypeViewModel.referencingByName.get(subLayoutView.opath)
      ?.objectType.name;
    let referencingColumn: string = this.settings.mainObjectTypeViewModel.referencingByName.get(subLayoutView.opath)
      ?.column?.name;

    const dataSourceInfo = await this.getGridOnFormDataSourceInfo(
      abcGridInstance,
      refObjectType,
      referencingColumn,
      subLayoutView.opath,
      'grid-' + this.service.arena.removeSpecialCharacter(subLayoutView.opath),
    );
    const otvm: ObjectTypeInfoViewModel = dataSourceInfo.objectTypeInfoViewModel;

    referencingColumn = dataSourceInfo.referencingIdColumn;
    const refColumns: _AbcGridColumn[] = [];
    const gridOnFormColumnObjects = this.settings.layoutViewModel?.gridOnFormColumnObjectMap
      ?.get(gridOnFormIndex)
      ?.entries();
    for (const columnObject of gridOnFormColumnObjects) {
      let layoutColumn = columnObject[1].LayoutColumn;
      let keyValue = columnObject[1].Key;
      let col: Object = columnObject[1];

      namedColumns[layoutColumn.OPath] = col;

      const column: _AbcGridColumn = new _AbcGridColumn();
      column.name = keyValue + '|' + layoutColumn.OPath;
      column.dataField = keyValue;
      column.caption = layoutColumn.GridColumnSettings.Caption;
      column.dataType = convertDataType(layoutColumn.DataType);
      column.visible = layoutColumn.OPath !== 'Id';
      column.format = _AbcGridComponent.getColumnFormat(column.dataType, layoutColumn.FormatSettings?.EditMask);
      column.visibleIndex = <number>layoutColumn.GridColumnSettings?.VisibleIndex;
      column.sortOrder = layoutColumn.GridColumnSettings?.SortOrder
        ? SortOrder[layoutColumn.GridColumnSettings?.SortOrder]
        : layoutColumn.GridColumnSettings?.SortIndex?.valueOf() >= 0
          ? SortOrder.Ascending
          : SortOrder.None;
      column.sortIndex = <number>layoutColumn.GridColumnSettings?.SortIndex;
      if (layoutColumn.IsReadOnly || layoutColumn.GridColumnSettings?.IsReadOnly) {
        column.allowEditing = false;
      }
      GridAdapter.prepareGridColumn(column);

      const objectTypeColumn = otvm.columnsByName.get(layoutColumn.OPath);

      if (!objectTypeColumn) {
        column.allowEditing = false;
      } else if (column.dataType === 'date' || column.dataType === 'datetime' || column.dataType === 'object') {
        column.editorOptions = {
          showClearButton: !objectTypeColumn.isRequired,
        };
      }

      if (layoutColumn.IsRef) {
        refColumns.push(column);

        column.setCustomCellEditor(this.service.abcCoreContainer, async (ddb: AbcDropDownBoxComponent, options) => {
          ddb.abcGrid.rtlEnabled = this.arenaService.rtlEnabled;
          const id: string = options.data['Id'];
          let initialValue = options.value;
          let initialDisplayText = this.service.prepareInitialDropDownText(initialValue, options.text);

          let customFilter: string;
          const posPoint = subLayoutView.opath?.lastIndexOf('.');
          if (posPoint >= 0 && layoutColumn.OPath === referencingColumn) {
            try {
              const query = new QueryObjectsModel();
              const oquery = new Query();
              oquery.OPath =
                'Type(' +
                (this.settings.mainObjectTypeViewModel?.name ?? this.settings.layoutViewModel.objectType) +
                ")[Id='" +
                this.settings.selectedObject?.Object?.Id +
                "']." +
                subLayoutView.opath.substring(0, posPoint);
              oquery.Columns = [
                {
                  OPath: 'Id',
                  Name: 'Id',
                },
              ];
              query.ObjectQueries = [oquery];
              const objects = ConvertHelper.arrayBufferToObjectArray(
                await this.settings.data.executeReadObjectsQuery(query).toPromise(),
              );
              const ids = objects.map((_) => _['Id']);
              if (!initialValue && ids?.length) {
                initialValue = ids[0];
                initialDisplayText = undefined;
                options.setValue(initialValue);
              }
              customFilter = "Id In ('" + ids.join("','") + "')";
            } catch (err) {
              console.error(err);
            }
          }

          await ddb.setupForRefColumn(
            otvm.name,
            layoutColumn.OPath,
            id,
            initialValue,
            initialDisplayText,
            false,
            dataSourceInfo.contextFilterExpression,
            () => {
              return this.getTargetColumnValuesFromGridRow(
                abcGridInstance?.getRowData(abcGridInstance.focusedRowIndex) ?? options?.data,
                namedColumns,
                referencingColumn,
                this.service.abcArena.currentDataEntity?.Id,
                posPoint >= 0,
              );
            },
            undefined,
            undefined,
            customFilter,
          );
          ddb.showClearButtonOnFocusOnly = !objectTypeColumn?.isRequired;
          ddb.onValueChanged.subscribe((value) => {
            options.setValue(ddb.value, ddb.text);
          });
          abcGridInstance.onRowPrepared.subscribe((_) => {
            const ds = ddb.dataSource;
            if (ds?.length && _.data) {
              const v = _.data[keyValue];
              let displayText: string;
              if (v) {
                for (const r of ds) {
                  if (r[<string>ddb.valueExpr] == v) {
                    displayText = r[<string>ddb.displayExpr];
                    break;
                  }
                }
              }
              _.data[this.service.DISPLAYTEXT_PREFIX + keyValue] = displayText;
            }
          });
        });
      } else if (column.dataType === 'datetime') {
        column.editCellTemplate = async (container, options) => {
          const c: AbcCoreComponent = this.service.abcCoreContainer.createComponent(null, AbcDateBoxComponent);
          container.append(c.domElement);
          const dateBox = <AbcDateBoxComponent>c.componentRef.instance;
          dateBox.type = 'time';
          dateBox.value = options.value;
          dateBox.onValueChanged.subscribe((value) => {
            options.setValue(dateBox.value);
          });
        };
      }
      columns.push(column);
    }

    const idColumn: _AbcGridColumn = new _AbcGridColumn();
    idColumn.name = 'Id';
    idColumn.dataField = 'Id';
    idColumn.caption = 'Id';
    idColumn.dataType = 'string';
    idColumn.visible = false;
    idColumn.showInColumnChooser = false;

    const objectTypeColumn: _AbcGridColumn = new _AbcGridColumn();
    objectTypeColumn.name = 'ObjectType';
    objectTypeColumn.caption = 'ObjectType';
    objectTypeColumn.dataField = 'ObjectType';
    objectTypeColumn.dataType = 'string';
    objectTypeColumn.visible = false;
    objectTypeColumn.showInColumnChooser = false;

    columns.push(idColumn, objectTypeColumn);

    this.gridOnRowInserting(abcGridInstance, otvm, referencingColumn, dataSourceInfo.referencingObjectTypeColumn);
    this.gridOnRowUpdating(abcGridInstance, otvm, referencingColumn, dataSourceInfo.referencingObjectTypeColumn);
    this.gridOnRowRemoving(abcGridInstance);

    // this.setTdcolAndRowSpan(td, subLayoutView);

    abcGridInstance.editing = {
      allowAdding: !denyInsert,
      allowDeleting: !denyDelete,
      allowUpdating: !denyUpdate,
      useIcons: true,
      confirmDelete: false,
    };

    this.settings.gridOnFormInstances.push(abcGridInstance);
    abcGridInstance.onInitNewRow.subscribe((e: InitNewRowEvent) => {
      this.settings.isGridOnFormOnEditing = true;
      // this.ensureSavingOnHignaIdChange = async () => {
      //   if (abcGridInstance?.instance?.hasEditData()) {
      //     await abcGridInstance.instance.saveEditData();
      //   }
      // };
      e.promise = new Promise((resolve, reject) => {
        if (this.service.abcArena.currentDataEntity?.Id) {
          resolve();
        } else {
          reject('Parent is empty');
        }
      });
    });

    abcGridInstance.onEditingStart.subscribe((e: EditingStartEvent) => {
      this.settings.isGridOnFormOnEditing = true;
      // this.ensureSavingOnHignaIdChange = async () => {
      //   if (abcGridInstance?.instance?.hasEditData()) {
      //     await abcGridInstance.instance.saveEditData();
      //   }
      // };
    });

    abcGridInstance.onEditCanceled.subscribe((e: EditCanceledEvent) => {
      this.settings.isGridOnFormOnEditing = false;
    });

    this.settings.setCurrentObjectFunctions.push(async (object) => {
      // if (this.ensureSavingOnHignaIdChange) {
      //   await this.ensureSavingOnHignaIdChange();
      // }
      abcGridInstance.columns = columns;

      if (object?.Id) {
        const parameters = [new ParameterValue('Id', object?.Id)];
        const query = this.service.createGridOnFormObjectsQuery(
          parameters,
          this.settings.mainObjectTypeViewModel?.name ?? this.settings.layoutViewModel.objectType,
          subLayoutView,
        );

        const gridOnFormObjects = ConvertHelper.arrayBufferToObjectArray(
          await this.service.data.readObjects(query).toPromise(),
        );

        for (const refColumn of refColumns) {
          const displayTextColumnName = this.service.DISPLAYTEXT_PREFIX + refColumn.dataField;
          if (!CompareHelper.isEmptyObject(gridOnFormObjects)) {
            for (const data of gridOnFormObjects) {
              const initialValue = data[refColumn.dataField];
              const initialDisplayText = data[displayTextColumnName];
              if (!initialDisplayText) {
                data[displayTextColumnName] = this.service.prepareInitialDropDownText(initialValue, initialDisplayText);
              }
            }
          }
          refColumn.setDisplayTextDataField(displayTextColumnName);
        }

        if (!CompareHelper.isEmptyObject(gridOnFormObjects)) {
          abcGridInstance.dataSource = gridOnFormObjects;
        } else {
          abcGridInstance.dataSource = [];
        }
      } else {
        abcGridInstance.dataSource = [];
      }
    });

    // abcGridRef.onRowValidating.subscribe((e) => {
    //   e.component.closeEditCell();
    //   const rowIndex = e.component.getRowIndexByKey(e.key);
    //   const StartDate = e.newData.StartDate === undefined ? e.oldData?.StartDate : e.newData?.StartDate;
    //   const EndDate = e.newData.EndDate === undefined ? e.oldData?.EndDate : e.newData?.EndDate;
    //   if (StartDate && EndDate) {
    //     if (new Date(StartDate) > new Date(EndDate)) {
    //       const cellElement = e.component
    //         .getCellElement(rowIndex, 'EndDate')
    //         .querySelector('.dx-datebox') as HTMLElement;
    //       cellElement.classList.add('dx-show-invalid-badge');
    //       cellElement.classList.add('dx-invalid');
    //       e.component.editCell(rowIndex, 0);
    //     }
    //   }
    // });

    return abcGrid;
  }

  private getTargetColumnValuesFromGridRow(
    data: any,
    namedColumns: object,
    referencingColumn: string,
    mainId: string,
    ignoreReferencingColumnIfExists: boolean,
  ): TargetColumnValue[] {
    const result: TargetColumnValue[] = [];
    let referencingColumnVisited: boolean = false;
    if (data) {
      for (const opath in namedColumns) {
        const col: object = namedColumns[opath];
        if (col && col['Key']) {
          let value = data[col['Key']];
          if (opath === referencingColumn) {
            if (mainId) {
              value = mainId;
              referencingColumnVisited = true;
            }
          }
          if (value) {
            result.push({
              Name: opath,
              Value: value,
            });
          }
        }
      }
    }
    if (
      !referencingColumnVisited &&
      referencingColumn &&
      mainId &&
      (!ignoreReferencingColumnIfExists || !namedColumns || Object.keys(namedColumns).includes(referencingColumn))
    ) {
      result.push({
        Name: referencingColumn,
        Value: mainId,
      });
    }
    return result;
  }

  private gridOnRowInserting(
    abcGrid: _AbcGridComponent,
    otvm: ObjectTypeInfoViewModel,
    referencingIdColumnName: string,
    referencingObjectTypeColumnName: string,
  ): Subscription {
    return this.createQueryAndPersist(
      abcGrid.onRowInserting,
      abcGrid,
      PersistMode.Insert,
      otvm,
      referencingIdColumnName,
      referencingObjectTypeColumnName,
    );
  }

  private gridOnRowUpdating(
    abcGrid: _AbcGridComponent,
    otvm: ObjectTypeInfoViewModel,
    referencingIdColumnName: string,
    referencingObjectTypeColumnName: string,
  ): Subscription {
    return this.createQueryAndPersist(
      abcGrid.onRowUpdating,
      abcGrid,
      PersistMode.Update,
      otvm,
      referencingIdColumnName,
      referencingObjectTypeColumnName,
    );
  }

  private gridOnRowRemoving(abcGrid: _AbcGridComponent): Subscription {
    return this.createQueryAndPersist(abcGrid.onRowRemoving, abcGrid, PersistMode.Delete);
  }
  private confirmDeletion(): Promise<boolean> {
    const _settings = new MessageSettings();
    _settings.mode = MessageMode.Question;
    _settings.icon = MessageBoxIcon.Question;
    _settings.button = MessageBoxButtons.YesNo;
    _settings.message = this.arenaService.res('Menu-ConfirmDeleteMessage');

    return this.service.messageBox
      .showMessageBox(_settings)
      .toPromise()
      .then((result) => {
        return DialogResult.Yes === result['result'];
      });
  }

  private createQueryAndPersist(
    eventEmitter: EventEmitter<RowUpdatingEvent | RowInsertingEvent | RowRemovingEvent>,
    abcGrid: _AbcGridComponent,
    persistMode: PersistMode,
    otvm?: ObjectTypeInfoViewModel,
    referencingIdColumnName?: string,
    referencingObjectTypeColumnName?: string,
  ): Subscription {
    return eventEmitter
      .pipe(
        tap((event) => {
          const proceedWithPersistence = () => {
            this.updateRequiredBooleanColumns(abcGrid, event, otvm);
            const data: IGridOnForm = {
              mainObject: this.settings.selectedObject?.Object,
              objectType: otvm?.name,
              referencingObjectTypeColumn: referencingObjectTypeColumnName,
              referencingIdColumn: referencingIdColumnName,
              data: this.compareDataAndPrepareForPersist(event, persistMode),
              abcGrid,
            };

            if (!CompareHelper.isEmptyObject(data.data) || persistMode !== PersistMode.Update) {
              const query: PersistObjectModel = this.service.createGridOnFormPersistQuery(data, persistMode);
              event.cancel = this.persistObject(query, data.data);
              event.cancel.then(
                (onSuccess) => {
                  this.settings.isGridOnFormOnEditing = false;
                },
                (onRejected) => {
                  this.settings.isGridOnFormOnEditing = true;
                },
              );
            }
          };

          if (persistMode === PersistMode.Delete) {
            event.cancel = true;
            this.confirmDeletion().then(async (confirmed) => {
              if (confirmed) {
                proceedWithPersistence();
              }
            });
          } else {
            proceedWithPersistence();
          }
        }),
      )
      .subscribe();
  }

  private compareDataAndPrepareForPersist(
    event: RowUpdatingEvent | RowInsertingEvent | RowRemovingEvent,
    persistMode: PersistMode,
  ): ICoreDataEntity & Object {
    let data: ICoreDataEntity & Object = { Id: '', ObjectType: '' };
    if (persistMode === PersistMode.Insert) {
      const e: RowInsertingEvent<ICoreDataEntity> = event as RowInsertingEvent<ICoreDataEntity>;
      data = e.data;
      console.log(data);
    } else if (persistMode === PersistMode.Update) {
      const e: RowUpdatingEvent<ICoreDataEntity> = event as RowUpdatingEvent<ICoreDataEntity>;
      for (const key in e.newData) {
        if (Object.prototype.hasOwnProperty.call(e.newData, key)) {
          const element = e.newData[key];

          if (e.oldData[key] !== element) {
            data[key] = element;
          }
        }
      }
      if (!CompareHelper.isEmptyObject(data)) {
        data.Id = e.oldData.Id;
        data.ObjectType = e.oldData.ObjectType;
      }
    } else if (persistMode === PersistMode.Delete) {
      const e: RowRemovingEvent<ICoreDataEntity> = event as RowRemovingEvent<ICoreDataEntity>;

      data.Id = e.data.Id;
      data.ObjectType = e.data.ObjectType;
    }

    return data;
  }

  private persistObject(query: PersistObjectModel, data: ICoreDataEntity & Object): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const persistedObject: ICoreDataEntity = ConvertHelper.arrayBufferToObject(
          await this.service.core.persist.executePersistObjectQuery(query).toPromise(),
        );
        if (persistedObject && data) {
          if (data.Id !== persistedObject.Id) {
            data.Id = persistedObject.Id;
          }
          if (!data.ObjectType) {
            data.ObjectType = persistedObject.ObjectType;
          }
          const objectKey = this.settings.objectKey;

          this.service.abcArena.actions.refreshArenaHignaObject({
            Id: objectKey.id,
            ObjectType: objectKey.objectType,
          });
        }

        this.service.arena.notificationSubject.next({
          type: ToastType.Success,
          message: this.service.arena.res('Arena-Success'),
        });
        // this.service.arena.callBindingComplete(this.settings.selectedObject.GridOnFormId);
        resolve();
      } catch (err) {
        reject(err.error.Message);
      }
    });
  }

  private updateRequiredBooleanColumns(abcGrid: _AbcGridComponent, event, otvm: ObjectTypeInfoViewModel) {
    if (otvm && event.data) {
      for (const gridColumn of abcGrid.columns) {
        if (typeof gridColumn !== 'string') {
          const opath = gridColumn.name.split('|')[1];
          const column = otvm.columnsByName.get(opath);
          if (
            column &&
            CompareHelper.isNullorEmpty(event.data[gridColumn.name]) &&
            column.isRequired &&
            column.infoObject.DataType === ObjectTypeInfoColumnDataType.Boolean
          ) {
            event.data[gridColumn.dataField] = false;
          }
        }
      }
    }
  }

  protected async getGridOnFormDataSourceInfo(
    abcGrid: _AbcGridComponent,
    refObjectType: string,
    referencingIdColumnName: string,
    subLayoutViewOpath?: string,
    gridHtmlElementId?: string,
  ): Promise<{
    objectTypeInfoViewModel: ObjectTypeInfoViewModel;
    contextFilterExpression: string;
    referencingObjectTypeColumn?: string;
    referencingIdColumn?: string;
  }> {
    const readListDSTargetColumns = [];

    let validatedOpath: ValidateModel;
    let refFilterExpression: string;
    let referencingObjectTypeColumnName: string;

    if (!refObjectType || subLayoutViewOpath?.indexOf('[') >= 0) {
      validatedOpath = ConvertHelper.arrayBufferToObject(await this.validateOpath(subLayoutViewOpath).toPromise());
    }

    if (!CompareHelper.isEmptyObject(validatedOpath)) {
      refObjectType = validatedOpath?.RefObjectTypeName;
      const segments = validatedOpath?.Segments;
      if (segments?.length) {
        segments.forEach((segment) => {
          if (segment?.ReferencingColumnName) {
            referencingIdColumnName = segment.ReferencingColumnName;
          }
          if (segment?.Filter) {
            refFilterExpression = segment.Filter;
          }
        });
        if (referencingIdColumnName) {
          readListDSTargetColumns.push({
            Name: referencingIdColumnName,
            Value: this.settings.selectedObject.Object?.Id,
          });
        } else {
          abcGrid.editing = {
            allowAdding: false,
          };
        }
      }
      this.settings.validatedSubLayoutViewsOpath.set(gridHtmlElementId, validatedOpath);
    }

    const otvm = await this.service.core.objectSpace.getObjectType(refObjectType).toPromise();

    if (referencingIdColumnName?.includes('Id')) {
      const candidate = referencingIdColumnName.replace('Id', 'ObjectType');

      if (candidate) {
        referencingObjectTypeColumnName = otvm.columnsByName.get(candidate)?.name;
      }
    }

    return {
      objectTypeInfoViewModel: otvm,
      contextFilterExpression: refFilterExpression,
      referencingObjectTypeColumn: referencingObjectTypeColumnName,
      referencingIdColumn: referencingIdColumnName,
    };
  }
}
